import { useMemo } from 'react';
import { useBudget } from '../store/BudgetContext';
import { theme } from '../theme';
import { Icon } from './Icon';
import { monthHeatmap } from '../utils/recording-streak';
import { frozenDates } from '../utils/game';

const WD = ['П', 'В', 'С', 'Ч', 'П', 'С', 'В'];

/**
 * Хитмап текущего месяца: серый — нет записей, светло-зелёный — записи есть,
 * зелёный — записи + день под бюджетом. Цепочку жалко рвать.
 */
export function Heatmap() {
  const { viewData } = useBudget();

  const days = useMemo(() => {
    if (!viewData) return [];
    return monthHeatmap(
      viewData.operations,
      viewData.categories,
      viewData.settings,
      frozenDates(viewData.game_events ?? []),
    );
  }, [viewData]);

  if (!viewData || days.length === 0) return null;

  const now = new Date();
  // Пн-первая сетка: сколько пустых ячеек перед 1-м числом
  const offset = (new Date(now.getFullYear(), now.getMonth(), 1).getDay() + 6) % 7;

  const cell = 34;

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(7, ${cell}px)`,
          gap: 5,
          justifyContent: 'center',
        }}
      >
        {WD.map((w, i) => (
          <div
            key={i}
            style={{
              textAlign: 'center',
              fontSize: 10,
              color: theme.colors.sub,
              letterSpacing: 1,
            }}
          >
            {w}
          </div>
        ))}
        {Array.from({ length: offset }, (_, i) => (
          <div key={`pad${i}`} />
        ))}
        {days.map((d) => {
          let bg: string = theme.colors.line;
          let fg: string = theme.colors.sub;
          let border = 'none';
          if (d.future) {
            bg = 'transparent';
            border = `1px dashed ${theme.colors.line}`;
          } else if (d.frozen) {
            bg = theme.colors.frost;
            fg = theme.colors.frostInk;
          } else if (d.recorded && d.underBudget) {
            bg = theme.colors.green;
            fg = theme.colors.paper;
          } else if (d.recorded) {
            bg = theme.colors.greenSoft;
            fg = theme.colors.ink;
          }
          return (
            <div
              key={d.iso}
              style={{
                height: cell,
                borderRadius: 0,
                background: bg,
                border,
                color: fg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                boxShadow: d.today ? `0 0 0 2px ${theme.colors.ink}` : 'none',
                boxSizing: 'border-box',
              }}
            >
              {d.frozen ? <Icon name="snow" size={13} color={fg} /> : d.dayNum}
            </div>
          );
        })}
      </div>
      <div
        style={{
          marginTop: 10,
          display: 'flex',
          gap: 14,
          justifyContent: 'center',
          fontSize: 11,
          color: theme.colors.sub,
        }}
      >
        <Legend color={theme.colors.greenSoft} label="записи" />
        <Legend color={theme.colors.green} label="записи + под бюджетом" />
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
      {label}
    </span>
  );
}
